import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, MapPin, Phone, ShieldCheck, Hammer } from "@phosphor-icons/react";
import { serviceImage } from "../../lib/api";
import { useContent, pick } from "../../lib/content";
import { useSite } from "../../lib/site";
import { useServices } from "../../lib/services";
import { findLocation, COMBO_SERVICE_SLUGS } from "../../lib/locations";
import { FAQS } from "../../lib/faqs";
import SEO from "../../components/SEO";
import CTABand from "../../components/public/CTABand";

export default function LocationDetail() {
  const { slug } = useParams();
  const loc = findLocation(slug);
  const c = useContent();
  const site = useSite();
  const services = useServices();

  if (!loc) return <Navigate to="/locations" replace />;

  const comboServices = services.filter((s) => COMBO_SERVICE_SLUGS.includes(s.slug));
  const businessName = site?.business_name || "ATC Home Pro's";
  const title = `Home Improvement & Construction in ${loc.full} — Decks, Kitchens, Roofing & More`;
  const description = `Local ${loc.name}, ${loc.state} contractor serving ${loc.region}. Custom decks, kitchens, bathrooms, roofing, siding, and concrete work — lifetime labor warranty on every project. Family-owned ATC Home Pro's, est. 1991.`;

  const baseUrl = (site?.site_url || "").replace(/\/$/, "");
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Home improvement & construction",
    "provider": { "@type": "GeneralContractor", "name": businessName, "url": baseUrl },
    "areaServed": { "@type": "Place", "name": loc.full, "address": { "@type": "PostalAddress", "addressLocality": loc.name, "addressRegion": loc.state, "addressCountry": "US" } },
    "name": `Home Improvement & Construction Services in ${loc.full}`,
    "description": description,
  };

  // FAQs trimmed to the most relevant for a location page + FAQPage schema
  const locFaqs = [
    {
      q: `Do you take projects in ${loc.full}?`,
      a: `Yes — ${loc.full} is well within our one-hour service radius from Glen Rock. We've worked nearby in ${loc.nearby.slice(0, 3).join(", ")}, and the crew handles every step in-house with the same lifetime labor warranty as our home-base jobs.`,
    },
    ...FAQS.slice(0, 5),
  ];
  const faqJson = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": locFaqs.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  return (
    <div data-testid={`location-page-${loc.slug}`}>
      <SEO path={`/locations/${loc.slug}`} title={title} description={description} jsonLd={jsonLd} />
      {/* Inject FAQPage as a second JSON-LD block via plain script */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJson) }} />

      {/* HERO */}
      <section className="bg-atc-black text-white pt-20 pb-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-blueprint-grid-dark bg-[size:4rem_4rem] opacity-30" />
        <div className="container-max relative">
          <div className="overline mb-4 text-atc-red flex items-center gap-2">
            <MapPin size={14} weight="bold" /> Serving {loc.full}
          </div>
          <h1 className="h-display">
            {businessName}<br />in {loc.name},<br/>
            <span className="text-atc-red">{loc.state}.</span>
          </h1>
          <p className="mt-6 text-white/80 text-lg max-w-2xl">{loc.anchor}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/contact" className="btn-primary" data-testid="location-cta-primary">
              Get a Free Estimate <ArrowUpRight size={18} weight="bold" />
            </Link>
            {site?.phone && (
              <a href={`tel:${site.phone}`} className="btn-ghost-white" data-testid="location-cta-call">
                <Phone size={16} weight="bold" /> {site.phone}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* WHY ATC IN THIS TOWN */}
      <section className="section-pad bg-atc-paper">
        <div className="container-max grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="overline mb-4">Why locals choose ATC</div>
            <h2 className="h-section">Local crew.<br />Lifetime warranty.</h2>
            <div className="rule-red mt-6" />
          </div>
          <div className="md:col-span-7">
            <p className="text-lg leading-relaxed text-atc-charcoal">
              {businessName} has been improving homes around {loc.region} since {site?.founding_year || 1991}. We pull permits with your local township, run our own crew (no subbing the job out), and back every project with a lifetime warranty on labor.
            </p>
            <p className="mt-5 text-lg leading-relaxed text-atc-charcoal">
              From quick repairs to whole-home renovations, homeowners in {loc.name} get the same attention to detail and the same fast response time as our Glen Rock neighbors.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="border-2 border-atc-black p-5">
                <ShieldCheck size={28} weight="bold" className="text-atc-red" />
                <div className="font-display text-2xl mt-3">Lifetime</div>
                <div className="font-mono text-[10px] uppercase tracking-widest mt-1 text-atc-muted">Labor Warranty</div>
              </div>
              <div className="border-2 border-atc-black p-5">
                <Hammer size={28} weight="bold" className="text-atc-red" />
                <div className="font-display text-2xl mt-3">In-House</div>
                <div className="font-mono text-[10px] uppercase tracking-widest mt-1 text-atc-muted">Crew Only</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TOP SERVICES FOR THIS TOWN */}
      {comboServices.length > 0 && (
        <section className="section-pad bg-white border-y-2 border-atc-black">
          <div className="container-max">
            <div className="mb-12">
              <div className="overline mb-4">Popular in {loc.name}</div>
              <h2 className="h-section">Top services for<br />{loc.name} homeowners.</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {comboServices.map((svc) => (
                <Link
                  key={svc.id}
                  to={`/services/${svc.slug}/${loc.slug}`}
                  className="group border-2 border-atc-black bg-white block transition-all hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[8px_8px_0_0_#1A1A1A]"
                  data-testid={`location-service-${svc.slug}`}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-atc-black">
                    <img src={serviceImage(svc)} alt={`${svc.title} in ${loc.full}`} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition" />
                  </div>
                  <div className="p-5">
                    <div className="overline mb-2">{loc.name}</div>
                    <h3 className="h-card group-hover:text-atc-red">{svc.title}</h3>
                    <div className="mt-3 inline-flex items-center gap-1 text-xs font-heading uppercase tracking-widest">
                      Learn more <ArrowUpRight size={12} weight="bold" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-10">
              <Link to="/services" className="btn-outline" data-testid="location-all-services">
                View all services <ArrowUpRight size={18} weight="bold" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* LOCATION-SPECIFIC FAQ */}
      <section className="section-pad bg-atc-paper">
        <div className="container-max grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="overline mb-4">FAQ</div>
            <h2 className="h-section">Common<br />questions.</h2>
            <p className="mt-5 text-atc-muted">Quick answers from real ATC homeowners.</p>
          </div>
          <div className="md:col-span-8">
            <dl className="divide-y-2 divide-atc-black border-2 border-atc-black bg-white">
              {locFaqs.map((f, i) => (
                <div key={i} className="p-6">
                  <dt className="font-heading uppercase tracking-wide text-atc-black">{f.q}</dt>
                  <dd className="mt-3 text-atc-charcoal leading-relaxed">{f.a}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <CTABand phone={site?.phone} />
    </div>
  );
}
